import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Home, Activity, Droplets, Waves, Container, Radar, Wind, LayoutGrid, Cpu,
  MapPin, Map as MapIcon, Bell, FileText, Users, ShieldCheck, Router,
  Settings2, Settings, LogOut, ChevronLeft, ChevronRight,
  ArrowDownToLine, ClipboardList, Lock,
  Zap, Radio, Building2, IndianRupee, Package, SlidersHorizontal, Award, Workflow, BellRing, AirVent, PlugZap,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { ROUTE_TO_MODULE } from "../../lib/moduleMap";
import { useSiteContext } from "./Layout";

// ─── Role sets ────────────────────────────────────────────────────────────────
// Listed from most-privileged down. A route with R.MANAGER is hidden from
// fire_safety_officer and viewer even though they are authenticated.

const R = {
  // null = no role gate; shown to every authenticated user
  ALL: null,

  // fire_safety_officer, site_manager, building_manager, admin, customer_admin, super_admin
  OPERATOR: ["super_admin", "platform_support", "admin", "customer_admin",
             "site_manager", "building_manager", "fire_safety_officer"],

  // site_manager, building_manager, admin, customer_admin, super_admin
  MANAGER:  ["super_admin", "platform_support", "admin", "customer_admin",
             "site_manager", "building_manager"],

  // admin, customer_admin, super_admin — full org administration
  ORG_ADMIN: ["super_admin", "platform_support", "admin", "customer_admin"],

  // super_admin / platform_support — platform-level only
  PLATFORM: ["super_admin", "platform_support"],
};

// ─── Menu definition ──────────────────────────────────────────────────────────
// Each item may carry a `roles` array from R above (null = all).
// The `pulse` flag shows a live-indicator dot.
// The `dynamicSite` flag replaces "/sites/" with "/sites/:currentSiteId/".

const menu = [
  {
    group: "Monitoring",
    items: [
      { to: "/dashboard",        label: "Dashboard",           icon: Home,            roles: R.ALL },
      { to: "/live-monitor",     label: "Live Monitor",        icon: Activity,        roles: R.ALL, pulse: true },
      { to: "/hydrant",          label: "Hydrant System",      icon: Droplets,        roles: R.ALL, siteModule: "hydrant" },
      { to: "/sprinkler",        label: "Sprinkler System",    icon: Waves,           roles: R.ALL, siteModule: "sprinkler" },
      { to: "/tank",             label: "Fire Water Tank",     icon: Container,       roles: R.ALL },
      { to: "/detection",        label: "Detection System",    icon: Radar,           roles: R.ALL, siteModule: "fire_alarm" },
      { to: "/ventilation",      label: "Ventilation System",  icon: Wind,            roles: R.ALL, siteModule: ["stairwell_pressurization", "basement_ventilation"] },
      { to: "/lifts-module",     label: "Lifts Module",        icon: ArrowDownToLine, roles: R.ALL, siteModule: "lifts" },
      { to: "/addressable-panel",label: "Addressable Panel",   icon: Radio,           roles: R.ALL, siteModule: "fire_alarm" },
      { to: "/control-management",  label: "Control Management",         icon: SlidersHorizontal, roles: R.ORG_ADMIN, pulse: true },
      { to: "/automation",          label: "Cause & Effect",             icon: Workflow,          roles: R.MANAGER, pulse: true },
    ],
  },
  {
    group: "BMS Monitoring",
    items: [
      // Hidden from client organisations (org admins and site users) — platform users only.
      { to: "/iaq-monitor",           label: "IAQ Monitor",              icon: Wind,          roles: R.PLATFORM },
      { to: "/energy-dashboard",      label: "Energy Meter Dashboard",   icon: Zap,           roles: R.PLATFORM },
      { to: "/work-orders",           label: "Work Order Management",    icon: ClipboardList, roles: R.PLATFORM, badgeKey: "open_work_orders" },
    ],
  },
  {
    group: "Asset Registries",
    items: [
      { to: "/pumps",                         label: "Pumps",               icon: Droplets,    roles: R.ALL },
      { to: "/registry/pressure-sensors",     label: "Pressure Sensors",    icon: ClipboardList, roles: R.ALL },
      { to: "/registry/tanks",                label: "Tanks",               icon: Container,   roles: R.ALL },
      { to: "/registry/valves",               label: "Valves",              icon: ClipboardList, roles: R.ALL },
      { to: "/registry/lifts",                label: "Lifts",               icon: ArrowDownToLine, roles: R.ALL },
      { to: "/registry/fans",                 label: "Fans & AHU",          icon: Wind,        roles: R.ALL },
      { to: "/registry/fire-alarm-devices",   label: "Fire Alarm Devices",  icon: Bell,        roles: R.ALL },
      { to: "/registry/mcc-panels",           label: "MCC Panels",         icon: Zap,         roles: R.ALL },
      // Platform only, like the IAQ Monitor and Energy Meter Dashboard they feed
      { to: "/registry/iaq-sensors",          label: "IAQ Sensors",         icon: AirVent,     roles: R.PLATFORM },
      { to: "/registry/energy-meters",        label: "Energy Meters",       icon: PlugZap,     roles: R.PLATFORM },
    ],
  },
  {
    group: "Management",
    items: [
      { to: "/panels",              label: "Panels Management",          icon: LayoutGrid, roles: R.MANAGER },
      { to: "/devices",             label: "Device Management",          icon: Cpu,        roles: R.MANAGER },
      { to: "/sites",               label: "Site Management",            icon: MapPin,     roles: R.ORG_ADMIN },
      { to: "/floors",              label: "Floor Plans",                icon: MapIcon,    roles: R.MANAGER },
      { to: "/gateways",            label: "Hardware Gateways",          icon: Router,     roles: R.ORG_ADMIN },
      { to: "/gateway-commands",    label: "Gateway Commands",           icon: Zap,        roles: R.ORG_ADMIN, pulse: true },
    ],
  },
  {
    group: "Operations",
    items: [
      { to: "/alerts",         label: "Alert Management",     icon: Bell,      roles: R.ALL, badgeKey: "open_alerts" },
      { to: "/alert-dispatch", label: "Alert Dispatch",       icon: BellRing,  roles: R.ORG_ADMIN, indent: true },
      { to: "/reports",        label: "Reports & Logs",   icon: FileText,  roles: R.OPERATOR, indent: true },
      { to: "/certificate",    label: "ACMS Certificates",    icon: Award,     roles: R.OPERATOR },
      // { to: "/fcc",            label: "Fire Command Center",  icon: Flame,     roles: R.ALL, external: true },
      { to: "/audit",          label: "Audit Log",            icon: FileText,  roles: R.ORG_ADMIN },
    ],
  },
  {
    group: "Administration",
    items: [
      { to: "/users",       label: "User Management",         icon: Users,       roles: R.ORG_ADMIN },
      { to: "/roles",       label: "Roles & Permissions",     icon: ShieldCheck, roles: R.ORG_ADMIN },
      { to: "/controller",  label: "Controller Settings",     icon: Settings2,   roles: R.ORG_ADMIN },
      { to: "/settings",    label: "System Settings",         icon: Settings,    roles: R.ORG_ADMIN },
    ],
  },
  {
    group: "Platform Admin",
    items: [
      { to: "/admin/organisations",    label: "Organisations",       icon: Building2,    roles: R.PLATFORM },
      // { to: "/admin/subscriptions",    label: "Org Subscriptions",   icon: ShieldCheck,  roles: R.PLATFORM },
      // { to: "/admin/module-catalogue", label: "Module Catalogue",    icon: Package,      roles: R.PLATFORM },
      // { to: "/admin/invoices",         label: "All Invoices",        icon: IndianRupee,  roles: R.PLATFORM },
    ],
  },
];

// ─── Role display labels ───────────────────────────────────────────────────────
const ROLE_LABEL = {
  super_admin:        "Super Admin",
  platform_support:   "Platform Support",
  admin:              "Admin",
  customer_admin:     "Org Admin",
  site_manager:       "Site Manager",
  building_manager:   "Building Manager",
  fire_safety_officer:"Safety Officer",
  sales:              "Sales",
  viewer:             "Viewer",
};

// ─── Sidebar component ────────────────────────────────────────────────────────
export default function Sidebar({ open, setOpen }) {
  const [openAlerts, setOpenAlerts] = useState(0);
  const [openWorkOrders, setOpenWorkOrders] = useState(0);
  const [subscribedKeys, setSubscribedKeys] = useState(null); // null = loading
  const [enabledModules, setEnabledModules] = useState(null); // null = no filter; Set = filter by these keys
  const { user, logout } = useAuth();
  const { selectedSite, sites, sitesLoaded, siteConfigVersion, myOrg, orgs, selectedOrg } = useSiteContext();
  const currentSiteId = selectedSite !== "all" ? selectedSite : null;
  const nav = useNavigate();

  // ── Derived role flags ──
  const role = user?.role;
  const isPlatform  = role === "super_admin" || role === "platform_support";
  const isOrgAdmin  = isPlatform || role === "admin" || role === "customer_admin";
  const isManager   = isOrgAdmin || role === "site_manager" || role === "building_manager";
  const isOperator  = isManager  || role === "fire_safety_officer";

  // ── Alert + work order badges — scoped to the header's org/site like the pages they open ──
  useEffect(() => {
    const params = {};
    if (isPlatform && selectedOrg) params.org_id = selectedOrg;
    if (currentSiteId) params.site_id = currentSiteId;
    const fetch = async () => {
      try {
        const r = await api.get("/alerts/summary", { params });
        setOpenAlerts(r.data.open || 0);
      } catch { /* ignore */ }
      try {
        const r = await api.get("/work-orders/summary", { params });
        setOpenWorkOrders((r.data.open || 0) + (r.data.in_progress || 0));
      } catch { /* ignore */ }
    };
    fetch();
    const i = setInterval(fetch, 30000);
    return () => clearInterval(i);
  }, [isPlatform, selectedOrg, currentSiteId]);

  // ── Subscription module keys (gates BMS / registry items) ──
  useEffect(() => {
    const fetch = async () => {
      try {
        const r = await api.get("/billing/my-subscription");
        setSubscribedKeys(new Set(r.data.modules.filter((m) => m.is_enabled).map((m) => m.module_key)));
      } catch { setSubscribedKeys(new Set()); }
    };
    fetch();
    const i = setInterval(fetch, 60000);
    return () => clearInterval(i);
  }, []);

  // ── Site module flags (gates Monitoring items by what's configured at the site) ──
  useEffect(() => {
    if (!sitesLoaded) return; // wait until GET /sites has actually completed

    const fetchModules = async () => {
      // Platform admin + all sites → bypass filter entirely
      if (isPlatform && (!selectedSite || selectedSite === "all")) {
        setEnabledModules(null);
        return;
      }

      // Org has no sites → hide all gated monitoring items
      if (!sites.length) {
        setEnabledModules(new Set());
        return;
      }

      // Which sites to query: specific selection, or all when nothing / "all" chosen
      const sitesToQuery = (!selectedSite || selectedSite === "all")
        ? sites
        : sites.filter((s) => s.site_id === selectedSite);

      if (!sitesToQuery.length) {
        setEnabledModules(new Set());
        return;
      }

      try {
        const configs = await Promise.all(
          sitesToQuery.map((s) =>
            api.get(`/site-config-full/${s.site_id}`).then((r) => r.data).catch(() => ({}))
          )
        );
        // Union: show item if enabled in ANY of the queried sites
        const union = new Set();
        configs.forEach((cfg) => {
          Object.entries(cfg.modules || {}).forEach(([k, v]) => { if (v) union.add(k); });
        });
        setEnabledModules(union);
      } catch {
        setEnabledModules(null); // on error don't hide anything
      }
    };

    fetchModules();
  }, [selectedSite, sites, sitesLoaded, isPlatform, siteConfigVersion]);

  // ── Visibility check: role gate + subscription gate + site module gate ──
  const canSee = (item) => {
    // 1. Role gate
    if (item.roles !== null && !item.roles.includes(role)) return false;
    // 2. Platform users bypass subscription + module checks when all sites selected
    if (isPlatform && enabledModules === null) return true;
    // 3. Subscription gate (items not in moduleMap are always shown)
    const mk = ROUTE_TO_MODULE[item.to];
    if (mk && subscribedKeys !== null && !subscribedKeys.has(mk)) return false;
    // 4. Site module gate — only applies when a config has loaded (non-null)
    if (item.siteModule && enabledModules !== null) {
      const keys = Array.isArray(item.siteModule) ? item.siteModule : [item.siteModule];
      if (!keys.some((k) => enabledModules.has(k))) return false;
    }
    return true;
  };

  // ── NavLink class helper ──
  const navClass = ({ isActive }) =>
    `group flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
      isActive
        ? "bg-orange-50 text-orange-600"
        : "text-gray-500 hover:text-gray-900 hover:bg-gray-50"
    }`;

  return (
    <aside
      className={`${open ? "w-64" : "w-20"} hidden md:flex shrink-0 h-screen fixed left-0 top-0 bg-white border-r border-gray-200 flex-col transition-all duration-300 z-40`}
      data-testid="sidebar"
    >
      {/* ── Logo / brand ── */}
      {/* Brand slot: an organisation's own users see their client logo (uploaded by the platform
          on the Organisations page), falling back to the org name; platform users see the
          Fire360 flame. The Fire360Pro wordmark lives at the header's right edge. */}
      <div className="flex items-center gap-3 px-4 py-4 border-b border-gray-200 min-h-[76px]">
        {(() => {
          // Whose logo to show: platform users see the organisation selected in the header (falling
          // back to their own org's); everyone else sees their own organisation's logo.
          const viewedOrg = isPlatform
            ? ((orgs || []).find((o) => o.org_id === selectedOrg) || myOrg?.org)
            : myOrg?.org;
          if (viewedOrg?.logo_url) {
            return (
              <div className="flex items-center gap-2.5 min-w-0" title={viewedOrg.name}>
                <img src={viewedOrg.logo_url} alt={viewedOrg.name || "Organisation logo"}
                     className={`object-contain shrink-0 transition-all ${open ? "h-10 max-w-[96px]" : "h-10 w-10"}`} />
                {open && viewedOrg.name && (
                  <span className="font-bold text-sm text-gray-900 leading-tight line-clamp-2">{viewedOrg.name}</span>
                )}
              </div>
            );
          }
          if (!isPlatform && viewedOrg?.name) {
            return (
              <div className="flex items-center gap-2.5 min-w-0" title={viewedOrg.name}>
                <div className="w-10 h-10 rounded-lg f360-gradient text-white font-extrabold flex items-center justify-center shrink-0">
                  {viewedOrg.name[0]?.toUpperCase()}
                </div>
                {open && <span className="font-bold text-sm text-gray-900 leading-tight line-clamp-2">{viewedOrg.name}</span>}
              </div>
            );
          }
          return (
            <div className="flex items-center gap-2.5 min-w-0">
              <img src="/favicon.svg" alt="Fire360Pro" className="w-10 h-10 shrink-0" />
              {open && <div className="leading-tight"><div className="font-extrabold text-sm text-gray-900">Fire360Pro</div><div className="text-[10px] uppercase tracking-wider text-gray-500">Platform Admin</div></div>}
            </div>
          );
        })()}
        {open && <div className="flex-1 min-w-0" />}
        <button
          onClick={() => setOpen(!open)}
          className="text-gray-400 hover:text-orange-500 transition"
          data-testid="sidebar-toggle-btn"
        >
          {open ? <ChevronLeft className="w-4 h-4" strokeWidth={2.5} /> : <ChevronRight className="w-4 h-4" strokeWidth={2.5} />}
        </button>
      </div>

      {/* ── Navigation ── */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-4 space-y-3 sidebar-scroll">

        {/* Regular menu groups */}
        {menu.map((g) => {
          const visibleItems = g.items.filter(canSee);
          if (visibleItems.length === 0) return null;
          return (
            <div key={g.group} data-testid={`section-${g.group.toLowerCase().replace(/\s+/g, "-")}`}>
              {open && (
                <div className="text-[10px] uppercase tracking-[0.15em] text-gray-400 font-bold px-3 mb-1.5">
                  {g.group}
                </div>
              )}
              <div className="space-y-0.5">
                {visibleItems.map((m) => {
                  const Icon = m.icon;
                  const resolvedTo = m.dynamicSite && currentSiteId
                    ? m.to.replace("/sites/", `/sites/${currentSiteId}/`)
                    : m.to;
                  return (
                    <NavLink
                      key={m.to}
                      to={resolvedTo}
                      data-testid={`nav-${m.to.replace(/^\//, "").replaceAll("/", "-")}`}
                      className={navClass}
                    >
                      <Icon className="w-4 h-4 shrink-0" />
                      {open && <span className="truncate flex-1">{m.label}</span>}
                      {open && m.pulse && (
                        <span className="w-2 h-2 rounded-full bg-emerald-500 status-pulse-green shrink-0" />
                      )}
                      {open && m.badgeKey === "open_alerts" && openAlerts > 0 && (
                        <span className="ml-auto bg-red-500/30 text-red-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                          {openAlerts}
                        </span>
                      )}
                      {open && m.badgeKey === "open_work_orders" && openWorkOrders > 0 && (
                        <span className="ml-auto bg-orange-500/30 text-orange-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                          {openWorkOrders}
                        </span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      {/* ── User footer ── */}
      <div className="border-t border-gray-200 p-3">
        {open ? (
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 rounded-full f360-gradient flex items-center justify-center text-white font-bold text-sm shrink-0">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-gray-900 truncate">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-wider text-orange-600">
                {ROLE_LABEL[role] || role}
              </div>
            </div>
            <button
              onClick={async () => { await logout(); nav("/login"); }}
              data-testid="logout-btn"
              className="text-gray-400 hover:text-red-500 shrink-0"
              title="Sign out"
            >
              <LogOut className="w-4 h-4" strokeWidth={2.5} />
            </button>
          </div>
        ) : (
          <button
            onClick={async () => { await logout(); nav("/login"); }}
            className="w-full flex justify-center py-2 text-gray-400 hover:text-red-500"
            title="Sign out"
          >
            <LogOut className="w-4 h-4" strokeWidth={2.5} />
          </button>
        )}
      </div>
    </aside>
  );
}
